const PRODUCT_KEY = 'gl-product-feed-v1';
const FEATURED_KEY = 'gl-featured-handles-v1';
const ARTICLE_KEY = 'gl-news-articles-v1';

const normalize = (value) => String(value || '').trim().toLowerCase();

const readCache = (key) => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const writeCache = (key, value) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    /* Session storage can reject oversized feeds. Matching still runs this view. */
  }
};

const scoreTags = (sourceTags, candidateTags) => {
  const wanted = new Set(sourceTags.map(normalize).filter(Boolean));
  const seen = new Set();
  let score = 0;
  (candidateTags || []).forEach((tag) => {
    const key = normalize(tag);
    if (!key || seen.has(key)) return;
    seen.add(key);
    if (wanted.has(key)) score += 1;
  });
  return score;
};

const loadProducts = async () => {
  const cached = readCache(PRODUCT_KEY);
  if (Array.isArray(cached) && cached.length) return cached;

  const products = [];
  for (let page = 1; page <= 40; page += 1) {
    const response = await fetch(`/products.json?limit=250&page=${page}`);
    if (!response.ok) throw new Error('catalog unavailable');
    const payload = await response.json();
    const batch = payload.products || [];
    batch.forEach((product) => {
      if (!product || !product.handle) return;
      products.push({ handle: product.handle, tags: product.tags || [] });
    });
    if (batch.length < 250) break;
  }

  if (!products.length) throw new Error('empty catalog');
  writeCache(PRODUCT_KEY, products);
  return products;
};

const loadFeaturedHandles = async () => {
  const cached = readCache(FEATURED_KEY);
  if (Array.isArray(cached) && cached.length) return cached;

  const handles = [];
  for (let page = 1; page <= 40; page += 1) {
    const response = await fetch(`/collections/all/products.json?limit=250&page=${page}`);
    if (!response.ok) throw new Error('featured catalog unavailable');
    const payload = await response.json();
    const batch = payload.products || [];
    batch.forEach((product) => {
      if (product && product.handle) handles.push(product.handle);
    });
    if (batch.length < 250) break;
  }

  if (!handles.length) throw new Error('empty featured catalog');
  writeCache(FEATURED_KEY, handles);
  return handles;
};

const loadArticles = async () => {
  const cached = readCache(ARTICLE_KEY);
  if (Array.isArray(cached)) return cached;

  const articles = [];
  for (let page = 1; page <= 40; page += 1) {
    const response = await fetch(`/blogs/news?view=gl-feed&page=${page}`);
    if (!response.ok) throw new Error('article feed unavailable');
    const payload = await response.json();
    articles.push(...(payload.articles || []));
    if (!payload.pages || page >= payload.pages) break;
  }

  writeCache(ARTICLE_KEY, articles);
  return articles;
};

const formatDate = (publishedOn) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(publishedOn || '');
  if (!match) return '';
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

const articleCard = (article) => {
  const card = document.createElement('article');
  card.className = 'gl-related-article';

  if (article.image) {
    const media = document.createElement('a');
    media.className = 'gl-related-article__media';
    media.href = article.url;
    media.tabIndex = -1;
    media.setAttribute('aria-hidden', 'true');
    const image = document.createElement('img');
    image.src = article.image;
    image.alt = article.alt || article.title || '';
    image.loading = 'lazy';
    media.appendChild(image);
    card.appendChild(media);
  }

  const body = document.createElement('div');
  body.className = 'gl-related-article__body';
  const tags = Array.isArray(article.tags) ? article.tags.slice(0, 3) : [];

  if (tags.length) {
    const list = document.createElement('ul');
    list.className = 'gl-article__tags';
    list.setAttribute('role', 'list');
    tags.forEach((tag) => {
      if (!tag || !tag.url) return;
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.className = 'gl-article__tag';
      link.href = tag.url;
      link.textContent = tag.label || '';
      item.appendChild(link);
      list.appendChild(item);
    });
    if (list.children.length) body.appendChild(list);
  }

  const meta = document.createElement('p');
  meta.className = 'gl-related-article__meta';
  const reading = document.createElement('span');
  reading.textContent = `${article.reading_time || 1} min read`;
  const date = document.createElement('span');
  date.textContent = formatDate(article.published_on);
  meta.append(reading, date);

  const title = document.createElement('h3');
  title.className = 'gl-related-article__title';
  const titleLink = document.createElement('a');
  titleLink.href = article.url;
  titleLink.textContent = article.title || '';
  title.appendChild(titleLink);

  body.append(meta, title);
  card.appendChild(body);
  return card;
};

const pickArticles = (articles, productTags) => {
  const ranked = articles.map((article, index) => ({
    article,
    index,
    score: scoreTags(
      productTags,
      (article.tags || []).map((tag) => tag && tag.label)
    ),
  }));

  const chosen = [];
  const used = new Set();
  ranked
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .forEach((entry) => {
      if (chosen.length >= 3) return;
      chosen.push(entry.article);
      used.add(entry.index);
    });

  if (chosen.length < 3) {
    ranked.forEach((entry) => {
      if (chosen.length >= 3 || used.has(entry.index)) return;
      chosen.push(entry.article);
      used.add(entry.index);
    });
  }

  return chosen;
};

const mountGearCards = async (grid, handles) => {
  const cards = await Promise.all(
    handles.map(async (handle) => {
      const response = await fetch(`/products/${encodeURIComponent(handle)}?view=gl-card`);
      if (!response.ok) return '';
      return response.text();
    })
  );

  cards.forEach((html) => {
    if (!html || !html.includes('data-gl-gear-card')) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const card = parsed.querySelector('[data-gl-gear-card] product-card');
    if (card) grid.appendChild(document.importNode(card, true));
  });
};

const hydrateGear = async () => {
  const root = document.querySelector('[data-gl-gear]');
  const grid = root && root.querySelector('[data-gl-gear-grid]');
  if (!root || !grid) return;

  try {
    const articleTags = JSON.parse(root.dataset.tags || '[]');
    const products = await loadProducts();
    const ranked = products
      .map((product, index) => ({ product, index, score: scoreTags(articleTags, product.tags) }))
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score || a.index - b.index);

    const handles = ranked.length
      ? ranked.slice(0, 4).map((entry) => entry.product.handle)
      : (await loadFeaturedHandles()).slice(0, 4);

    await mountGearCards(grid, handles);
    if (!grid.children.length) root.remove();
    else root.hidden = false;
  } catch (error) {
    root.remove();
  }
};

const hydrateProductArticles = async () => {
  const root = document.querySelector('[data-gl-product-articles]');
  const grid = root && root.querySelector('[data-gl-product-articles-grid]');
  if (!root || !grid) return;

  try {
    const productTags = JSON.parse(root.dataset.tags || '[]');
    const articles = await loadArticles();
    if (!articles.length) {
      root.remove();
      return;
    }

    pickArticles(articles, productTags).forEach((article) => {
      grid.appendChild(articleCard(article));
    });

    if (!grid.children.length) root.remove();
    else root.hidden = false;
  } catch (error) {
    root.remove();
  }
};

hydrateGear();
hydrateProductArticles();
